import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Search, Folder, ArrowRight, Check } from 'lucide-react';
import { clsx } from 'clsx';
import { Session } from '@/types/sessions.types';
import { useExtensionUtil } from '@/hooks/useExtensionUtil';
import { useLoadSessions } from '@/hooks/useLoadSessions';
import { useSessionsStore } from '@/stores/sessions-store';
import { useDialogA11y } from '@/hooks/useDialogA11y';
import { loadSessionsViaExtensionOrApi } from '@/utils/extensionSessions';

interface MoveSessionModalProps {
  isOpen: boolean;
  sessionName: string;
  currentSessionId: string;
  onClose: () => void;
  onSelect: (targetSessionId: string, targetSessionName: string, isWorkSpace: boolean) => void;
}

const getEmoji = (icon?: string) => {
  if (!icon) return null;
  if (icon.length <= 2 || !/^[0-9A-Fa-f]+$/.test(icon)) {
    return icon;
  }
  try {
    const codePoint = parseInt(icon, 16);
    return String.fromCodePoint(codePoint);
  } catch {
    return icon;
  }
};

export const MoveSessionModal: React.FC<MoveSessionModalProps> = ({
  isOpen,
  sessionName,
  currentSessionId,
  onClose,
  onSelect
}) => {
  const { sendCBMessage } = useExtensionUtil();
  const { loadSessions } = useLoadSessions();
  const allSessions = useSessionsStore((s) => s.allSessions);
  const { t } = useTranslation('common');
  const [searchQuery, setSearchQuery] = useState('');
  const [unsavedSessions, setUnsavedSessions] = useState<Session[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [isWorkSpace, setIsWorkSpace] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState(false);
  const dialogRootRef = useRef<HTMLDivElement | null>(null);

  useDialogA11y(dialogRootRef, { isOpen, onClose });

  const projects = allSessions.filter((s) => s.isWorkSpace === true);

  useEffect(() => {
    if (isOpen) {
      setIsLoading(true);
      void loadSessions();

      void loadSessionsViaExtensionOrApi(sendCBMessage).then(({ sessions, accountMismatch }) => {
        if (accountMismatch) {
          setUnsavedSessions([]);
        } else {
          setUnsavedSessions(sessions.filter((s) => s.isWorkSpace === false));
        }
        setIsLoading(false);
      });
    } else {
      setSearchQuery('');
      setSelectedSessionId(null);
      setIsWorkSpace(true);
    }
  }, [isOpen, sendCBMessage, loadSessions]);

  const filteredProjects = projects.filter(session =>
    session.sid !== currentSessionId && session.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredUnsaved = unsavedSessions.filter(session =>
    session.sid !== currentSessionId && session.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelect = () => {
    if (selectedSessionId) {
      const selectedSession = isWorkSpace
        ? projects.find(s => s.sid === selectedSessionId)
        : unsavedSessions.find(s => s.sid === selectedSessionId);
      
      if (selectedSession) {
        onSelect(selectedSessionId, selectedSession.name, isWorkSpace);
        onClose();
      }
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        ref={dialogRootRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="move-session-modal-title"
        tabIndex={-1}
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-lg z-50 focus:outline-none"
      >
        <div className="bg-card border border-border modal-surface shadow-modal overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-border-muted">
            <div className="flex-1">
              <h2 id="move-session-modal-title" className="text-base font-semibold text-foreground">
                {t('shared.modals.moveSession.title')}
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                {t('shared.modals.moveSession.subtitle', { name: sessionName })}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label={t('common.close')}
              className="text-muted-foreground hover:text-foreground transition-colors p-1.5 hover:bg-muted rounded-lg"
            >
              <X size={18} aria-hidden />
            </button>
          </div>

          {/* Search */}
          <div className="px-4 py-3 border-b border-border-muted">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/60" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('shared.modals.moveSession.searchPlaceholder')}
                className="w-full pl-9 pr-3 py-2 bg-muted/50 border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all"
                autoFocus
              />
            </div>
          </div>

          {/* Session Lists */}
          <div className="max-h-[360px] overflow-y-auto custom-scrollbar">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              </div>
            ) : filteredUnsaved.length === 0 && filteredProjects.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
                <Folder size={40} className="text-muted-foreground/30 mb-2" />
                <p className="text-sm font-medium text-muted-foreground">
                  {searchQuery ? t('shared.modals.moveSession.noWorkspacesFound') : t('shared.modals.moveSession.noWorkspacesAvailable')}
                </p>
                {searchQuery && (
                  <p className="text-xs text-muted-foreground/60 mt-0.5">
                    {t('shared.modals.moveSession.tryDifferent')}
                  </p>
                )}
              </div>
            ) : (
              <div className="py-1">
                {/* UNSAVED Section */}
                {filteredUnsaved.length > 0 && (
                  <div className="mb-2">
                    <div className="px-4 py-2">
                      <h3 className="text-[10px] font-bold tracking-wider uppercase text-muted-foreground/50">
                        {t('shared.modals.moveSession.unsaved')}
                      </h3>
                    </div>
                    <div className="px-2 space-y-0.5">
                      {filteredUnsaved.map((session) => {
                        const emoji = getEmoji(session.icon);
                        const isSelected = selectedSessionId === session.sid && !isWorkSpace;
                        
                        return (
                          <button
                            key={session.sid}
                            onClick={() => {
                              setSelectedSessionId(session.sid);
                              setIsWorkSpace(false);
                            }}
                            className={clsx(
                              "w-full flex items-center gap-2.5 py-2 px-3 rounded-lg transition-all group",
                              isSelected
                                ? "bg-primary/10 text-primary ring-1 ring-primary/30"
                                : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                            )}
                          >
                            <div className={clsx(
                              "p-1.5 rounded-md transition-colors flex items-center justify-center w-7 h-7 flex-shrink-0",
                              isSelected ? "bg-primary/20 text-primary" : "bg-muted text-muted-foreground group-hover:text-foreground/80"
                            )}>
                              {emoji ? (
                                <span className="text-sm leading-none">{emoji}</span>
                              ) : (
                                <Folder size={14} className={clsx(isSelected && "fill-primary/10")} />
                              )}
                            </div>
                            <span className={clsx(
                              "text-sm truncate flex-1 text-left",
                              isSelected ? "font-semibold text-foreground" : "font-medium"
                            )}>
                              {session.name}
                            </span>
                            {isSelected && (
                              <Check size={16} className="text-primary flex-shrink-0" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* PROJECTS Section */}
                {filteredProjects.length > 0 && (
                  <div>
                    <div className="px-4 py-2">
                      <h3 className="text-[10px] font-bold tracking-wider uppercase text-muted-foreground/50">
                        {t('shared.modals.moveSession.projects')}
                      </h3>
                    </div>
                    <div className="px-2 space-y-0.5">
                      {filteredProjects.map((session) => {
                        const emoji = getEmoji(session.icon);
                        const isSelected = selectedSessionId === session.sid && isWorkSpace;
                        
                        return (
                          <button
                            key={session.sid}
                            onClick={() => {
                              setSelectedSessionId(session.sid);
                              setIsWorkSpace(true);
                            }}
                            className={clsx(
                              "w-full flex items-center gap-2.5 py-2 px-3 rounded-lg transition-all group",
                              isSelected
                                ? "bg-primary/10 text-primary ring-1 ring-primary/30"
                                : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                            )}
                          >
                            <div className={clsx(
                              "p-1.5 rounded-md transition-colors flex items-center justify-center w-7 h-7 flex-shrink-0",
                              isSelected ? "bg-primary/20 text-primary" : "bg-muted text-muted-foreground group-hover:text-foreground/80"
                            )}>
                              {emoji ? (
                                <span className="text-sm leading-none">{emoji}</span>
                              ) : (
                                <Folder size={14} className={clsx(isSelected && "fill-primary/10")} />
                              )}
                            </div>
                            <span className={clsx(
                              "text-sm truncate flex-1 text-left",
                              isSelected ? "font-semibold text-foreground" : "font-medium"
                            )}>
                              {session.name}
                            </span>
                            {isSelected && (
                              <Check size={16} className="text-primary flex-shrink-0" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-border-muted">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-all"
            >
              {t('shared.modals.moveSession.cancel')}
            </button>
            <button
              onClick={handleSelect}
              disabled={!selectedSessionId}
              className={clsx(
                "px-4 py-2 text-sm font-semibold rounded-lg transition-all flex items-center gap-1.5",
                selectedSessionId
                  ? "bg-primary text-primary-foreground hover:bg-primary/90"
                  : "bg-muted text-muted-foreground/40 cursor-not-allowed"
              )}
            >
              {t('shared.modals.moveSession.move')}
              <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: hsl(var(--muted-foreground) / 0.15);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: hsl(var(--muted-foreground) / 0.3);
        }
      `}} />
    </>
  );
};
